import { getCsrfToken } from "@/app/_utils/csrf";

export type MerchShowLink = {
  show_id: string;
  title: string;
  variant_name?: string | null;
  start?: string | null;
  exclusive: boolean;
};

export type MerchShowOption = {
  id: string;
  title: string;
  start: string;
  img_url?: string | null;
};

class MerchService {
  static instance: MerchService;
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;

  // Read per call: the cookie only exists once there is a session, which is after this
  // module first loads, so a field initialiser can capture "" and never recover.
  get csrfToken() {
    return getCsrfToken();
  }

  getMerchAsync = async (id: string) => {
    const res = await fetch(`${this.baseUrl}merch/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getMerchAnalyticsAsync = async (id: string) => {
    const res = await fetch(`${this.baseUrl}merch/${id}/analytics/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getAllMerchAsync = async () => {
    const res = await fetch(`${this.baseUrl}merch/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  createMerchAsync = async (merch: any) => {
    const res = await fetch(`${this.baseUrl}merch/`, {
      method: "POST",
      body: JSON.stringify(merch),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  updateMerchAsync = async (id: string, merch: any) => {
    const res = await fetch(`${this.baseUrl}merch/${id}/`, {
      method: "PUT",
      body: JSON.stringify(merch),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return res;
  };

  publishMerchAsync = async (id: string, data: any) => {
    const res = await fetch(`${this.baseUrl}merch/${id}/publish/`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return res;
  };

  deleteMerchAsync = async (id: string) => {
    return await fetch(`${this.baseUrl}merch/${id}/`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
      },
    });
  };

  getOrders = async (id: string) => {
    const res = await fetch(`${this.baseUrl}merch/${id}/orders/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  getShowLinksAsync = async (id: string): Promise<MerchShowLink[]> => {
    const res = await fetch(`${this.baseUrl}merch/${id}/shows/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  setShowLinksAsync = async (
    id: string,
    shows: { show_id: string; exclusive: boolean }[]
  ): Promise<MerchShowLink[]> => {
    const res = await fetch(`${this.baseUrl}merch/${id}/shows/`, {
      method: "PUT",
      body: JSON.stringify({ shows }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    if (!res.ok) throw new Error("Failed to save shows");
    return await res.json();
  };

  getShowOptionsAsync = async (): Promise<MerchShowOption[]> => {
    const res = await fetch(`${this.baseUrl}merch/show-options/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  exportMerchOrders = async (id: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append("export", true.toString());

    const res = await fetch(
      `${this.baseUrl}merch/${id}/orders/?${queryParams}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return await res.blob();
  };
}

export default new MerchService();
